import React, { useState, useRef, useEffect } from "react";
import { Pagination } from "@mui/material";
import { debounce } from "../commonFiles/debounce";

interface PaginationComponentProps {
  offset: number;
  limit: number;
  totalCount: number;
  onPageChange: (newOffset: number) => void;
  className?: string;
}

const PaginationComponent: React.FC<PaginationComponentProps> = ({
  offset,
  limit,
  totalCount,
  onPageChange,
  className = "paginationDiv"
}) => {
  const [pageInput, setPageInput] = useState("");
  const [error, setError] = useState("");
  const [errorPosition, setErrorPosition] = useState("below");
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate pagination values
  const totalPages = Math.ceil(totalCount / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  const fromVal = totalCount > 0 ? offset + 1 : 0;
  const toVal = Math.min(offset + limit, totalCount);

  // Update error position on window resize and orientation change
  useEffect(() => {
    const updateErrorPosition = () => {
      if (error) {
        checkErrorPosition();
      }
    };

    const debouncedUpdate = debounce(updateErrorPosition, 100);
    window.addEventListener("resize", debouncedUpdate);
    window.addEventListener("orientationchange", debouncedUpdate);

    return () => {
      window.removeEventListener("resize", debouncedUpdate);
      window.removeEventListener("orientationchange", debouncedUpdate);
    };
  }, [error]);

  const checkErrorPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < 100 && spaceAbove > 100) {
        setErrorPosition("above");
      } else {
        setErrorPosition("below");
      }
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    const newOffset = (page - 1) * limit;
    onPageChange(newOffset);
  };

  const handlePageJump = () => {
    const pageNumber = parseInt(pageInput);
    
    if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > totalPages) {
      setError(`Please enter a page number between 1 and ${totalPages}`);
      checkErrorPosition();
      return;
    }

    setError("");
    const newOffset = (pageNumber - 1) * limit;
    onPageChange(newOffset);
    setPageInput("");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handlePageJump();
    }
  };

  if (totalCount === 0) {
    return null;
  }

  return (
    <div className={className}>
      <span>
        Showing {fromVal} to {toVal} of {totalCount} entries
      </span>
      <div className="pagination-controls">
        <Pagination
          size="small"
          shape="rounded"
          count={totalPages}
          color="primary"
          page={currentPage}
          onChange={handlePageChange}
          defaultPage={1}
          siblingCount={1}
        />
        <PageJumpControl
          ref={containerRef}
          pageInput={pageInput}
          setPageInput={setPageInput}
          totalPages={totalPages}
          onKeyPress={handleKeyPress}
          onPageJump={handlePageJump}
          error={error}
          errorPosition={errorPosition}
        />
      </div>
    </div>
  );
};

// Page Jump Control Component
interface PageJumpControlProps {
  ref: React.RefObject<HTMLDivElement>;
  pageInput: string;
  setPageInput: (value: string) => void;
  totalPages: number;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onPageJump: () => void;
  error: string;
  errorPosition: string;
}

const PageJumpControl = React.forwardRef<HTMLDivElement, PageJumpControlProps>(
  ({ pageInput, setPageInput, totalPages, onKeyPress, onPageJump, error, errorPosition }, ref) => {
    return (
      <div ref={ref} className="page-jump-control position-relative">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">Go to:</span>
          <input
            type="number"
            min="1"
            max={totalPages}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onKeyPress={onKeyPress}
            className="form-control form-control-sm"
            style={{ width: "60px" }}
            placeholder="Page"
          />
          <button
            onClick={onPageJump}
            className="btn btn-sm btn-outline-primary"
            disabled={!pageInput}
          >
            Go
          </button>
        </div>
        {error && (
          <div
            className={`error-tooltip position-absolute ${
              errorPosition === "above" ? "bottom-100 mb-2" : "top-100 mt-2"
            } start-0`}
            style={{
              backgroundColor: "#dc3545",
              color: "white",
              padding: "4px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              whiteSpace: "nowrap",
              zIndex: 1000,
            }}
          >
            {error}
          </div>
        )}
      </div>
    );
  }
);

export default PaginationComponent;
